import React, { useEffect, useState } from 'react';
import api from '../services/api';

const CHANNELS = ['dev', 'staff', 'citizen'];

const AdminConnectorPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [config, setConfig] = useState(null);
  const [comsappEnabled, setComsappEnabled] = useState(true);
  const [defaultComsappBaseUrl, setDefaultComsappBaseUrl] = useState('');
  const [provisionStatus, setProvisionStatus] = useState(null);
  const [verifying, setVerifying] = useState(null);
  const [verifyMessages, setVerifyMessages] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const configRes = await api.get('/api/connector/config');
        const nextConfig = configRes?.data?.config || null;
        setConfig(nextConfig);
        setComsappEnabled(nextConfig?.comsappEnabled !== false);
        setDefaultComsappBaseUrl(configRes?.data?.defaultComsappBaseUrl || '');

        // Fetch provision status to show admin summary
        const provisionRes = await api
          .post('/api/connector/provision-me')
          .then((res) => res?.data || { success: false })
          .catch(() => ({ success: false }));
        setProvisionStatus(provisionRes);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load connector config.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleVerifySlug = async (channelKey) => {
    const channel = config?.channels?.[channelKey];
    if (!channel?.slug) {
      setVerifyMessages((prev) => ({ ...prev, [channelKey]: { type: 'error', text: 'No slug configured.' } }));
      return;
    }

    setVerifying(channelKey);
    setVerifyMessages((prev) => ({ ...prev, [channelKey]: { type: 'info', text: 'Verifying slug...' } }));

    try {
      const res = await api.post('/api/connector/verify-slug', {
        channelKey,
        slug: channel.slug,
      });

      if (res.data?.success) {
        setVerifyMessages((prev) => ({
          ...prev,
          [channelKey]: { type: 'success', text: `✓ Verified: Space ID ${res.data.spaceId}` },
        }));
        // Reload config to reflect verified spaceId
        const updatedRes = await api.get('/api/connector/config');
        setConfig(updatedRes?.data?.config || null);
      } else {
        setVerifyMessages((prev) => ({
          ...prev,
          [channelKey]: { type: 'error', text: res.data?.message || 'Verification failed.' },
        }));
      }
    } catch (err) {
      setVerifyMessages((prev) => ({
        ...prev,
        [channelKey]: {
          type: 'error',
          text: err.response?.data?.message || 'Could not verify slug. Check ComSapp connectivity.',
        },
      }));
    } finally {
      setVerifying(null);
    }
  };

  const handleToggleChannel = async (channelKey, enabled) => {
    try {
      const updatedChannels = {
        ...config?.channels,
        [channelKey]: {
          ...config?.channels?.[channelKey],
          enabled: !enabled,
        },
      };

      const res = await api.put('/api/connector/config', {
        comsappBaseUrl: config?.comsappBaseUrl,
        comsappEnabled,
        channels: updatedChannels,
      });

      if (res.data?.success) {
        setConfig(res.data?.config);
        setVerifyMessages((prev) => ({
          ...prev,
          [channelKey]: {
            type: 'success',
            text: `✓ Channel ${!enabled ? 'enabled' : 'disabled'}.`,
          },
        }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update channel.');
    }
  };

  const messageStyle = (type) => {
    const styles = {
      success: { color: '#86efac', fontSize: 12 },
      error: { color: '#fca5a5', fontSize: 12 },
      info: { color: '#93c5fd', fontSize: 12 },
      warn: { color: '#fbbf24', fontSize: 12 },
    };
    return styles[type] || styles.info;
  };

  const channelBoxStyle = (enabled) => ({
    background: enabled ? '#1f2937' : '#111827',
    border: enabled ? '1px solid #10b981' : '1px solid #475569',
    borderRadius: 8,
    padding: 14,
    marginBottom: 12,
    opacity: enabled ? 1 : 0.6,
  });

  return (
    <div style={{ padding: '28px 32px', maxWidth: 960, margin: '0 auto', color: '#e2e8f0' }}>
      <h2 style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9', margin: 0 }}>Connector Configuration</h2>
      <p style={{ color: '#94a3b8', marginTop: 8, marginBottom: 24 }}>
        Manage ComSapp channel mappings for this platform.
      </p>

      {loading && <p style={{ color: '#94a3b8' }}>Loading configuration...</p>}
      {error && <p style={{ color: '#fca5a5', fontWeight: 500 }}>{error}</p>}

      {!loading && !error && (
        <div style={{ display: 'grid', gap: 20 }}>
          {/* System Info Card */}
          <div style={{ background: '#111827', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>System Configuration</div>
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#cbd5e1' }}>ComS Master Switch:</span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={comsappEnabled}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      setComsappEnabled(next);
                      try {
                        const res = await api.patch('/api/connector/coms-enabled', { enabled: next });
                        if (res.data?.success) {
                          setConfig(res.data?.config);
                          window.dispatchEvent(new Event('coms-config-changed'));
                        }
                      } catch (err) {
                        setComsappEnabled((prev) => !prev);
                        setError(err.response?.data?.message || 'Failed to update ComS master switch.');
                      }
                    }}
                  />
                  <span style={{ fontSize: 12, color: comsappEnabled ? '#86efac' : '#fca5a5', fontWeight: 700 }}>
                    {comsappEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#cbd5e1' }}>App ID:</span>
                <span style={{ fontSize: 13, color: '#f1f5f9', fontFamily: 'monospace', background: '#0f172a', padding: '2px 8px', borderRadius: 4 }}>
                  cip
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#cbd5e1' }}>ComSapp Base URL:</span>
                <span style={{ fontSize: 13, color: '#60a5fa', fontFamily: 'monospace' }}>
                  {config?.comsappBaseUrl || defaultComsappBaseUrl || 'Not set'}
                </span>
              </div>
              {config?.updatedAt && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, color: '#cbd5e1' }}>Last Updated:</span>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>
                    {new Date(config.updatedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Channel Configuration Cards */}
          <div>
            <div style={{ fontSize: 13, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>
              Channel Mappings
            </div>
            <div style={{ display: 'grid', gap: 12 }}>
              {CHANNELS.map((channelKey) => {
                const channel = config?.channels?.[channelKey];
                const enabled = channel?.enabled !== false;
                const hasSpaceId = !!channel?.spaceId;
                const verifyMsg = verifyMessages[channelKey];

                return (
                  <div key={channelKey} style={channelBoxStyle(enabled)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', textTransform: 'capitalize' }}>
                          {channelKey}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                          {channel?.label || `${channelKey} channel`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => handleToggleChannel(channelKey, enabled)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span style={{ fontSize: 12, color: '#cbd5e1' }}>{enabled ? 'Enabled' : 'Disabled'}</span>
                        </label>
                      </div>
                    </div>

                    {enabled && (
                      <div style={{ display: 'grid', gap: 10 }}>
                        {/* Slug Input + Verify */}
                        <div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Slug (Space identifier)</div>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                            <input
                              type="text"
                              value={channel?.slug || ''}
                              disabled
                              style={{
                                flex: 1,
                                padding: '6px 10px',
                                background: '#0f172a',
                                border: '1px solid #334155',
                                borderRadius: 4,
                                color: '#cbd5e1',
                                fontSize: 12,
                                fontFamily: 'monospace',
                              }}
                            />
                            <button
                              onClick={() => handleVerifySlug(channelKey)}
                              disabled={!channel?.slug || verifying === channelKey}
                              style={{
                                padding: '6px 12px',
                                background: hasSpaceId ? '#10b981' : '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: verifying === channelKey ? 'wait' : 'pointer',
                                opacity: !channel?.slug ? 0.5 : 1,
                              }}
                            >
                              {verifying === channelKey ? '...' : hasSpaceId ? '✓ Verified' : 'Verify'}
                            </button>
                          </div>
                          {verifyMsg && <div style={messageStyle(verifyMsg.type)}>{verifyMsg.text}</div>}
                        </div>

                        {/* Space ID (readonly if verified) */}
                        {hasSpaceId && (
                          <div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>Canonical Space ID (verified)</div>
                            <input
                              type="text"
                              value={channel.spaceId}
                              disabled
                              style={{
                                width: '100%',
                                padding: '6px 10px',
                                background: '#0f172a',
                                border: '1px solid #10b981',
                                borderRadius: 4,
                                color: '#86efac',
                                fontSize: 12,
                                fontFamily: 'monospace',
                              }}
                            />
                            {channel?.spaceName && (
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                                Space Name: <strong style={{ color: '#cbd5e1' }}>{channel.spaceName}</strong>
                              </div>
                            )}
                            {channel?.lastVerifiedAt && (
                              <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                                Verified {new Date(channel.lastVerifiedAt).toLocaleString()}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Provision Summary */}
                        {provisionStatus?.provisioned && (
                          <div style={{ fontSize: 11, color: '#94a3b8', padding: 8, background: '#0f172a', borderRadius: 4 }}>
                            Provision: {provisionStatus.provisioned.length} user{provisionStatus.provisioned.length === 1 ? '' : 's'} in this channel
                          </div>
                        )}
                      </div>
                    )}

                    {enabled && !comsappEnabled && (
                      <div style={{ fontSize: 12, color: '#fca5a5', fontStyle: 'italic' }}>
                        ComS master switch is disabled. Channel mappings are saved, but provisioning/access is off.
                      </div>
                    )}

                    {!enabled && (
                      <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                        This channel is disabled. Users will not be provisioned or see this space.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Diagnostics */}
          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
              <strong>Troubleshooting:</strong>
            </div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#cbd5e1', lineHeight: 1.6 }}>
              <li>Ensure all slugs match the exact space identifiers in ComSapp.</li>
              <li>Click Verify to cache the canonical spaceId for faster redirects.</li>
              <li>Disable channels to hide them from users (e.g., during maintenance).</li>
              <li>Check ComSapp server logs if verify fails or provision shows errors.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminConnectorPage;
